/**
 * Réglages des notifications
 *
 * Écran clé, à ne pas traiter comme une simple liste d'interrupteurs. Il montre
 * **l'heure réelle d'envoi**, silence compris : si le résumé est réglé à 23 h et
 * que la nuit est silencieuse jusqu'à 7 h, la ligne l'affiche. Un réglage dont
 * l'effet n'est pas visible est un réglage qui trompe.
 *
 * Le bouton « envoyer un test » par canal donne un retour utilisable : quand ça
 * ne marche pas, il dit lequel des trois n'est pas configuré et ce qui manque.
 */

import { useEffect, useState } from 'react';
import { BellRing, Mail, Send, Smartphone } from 'lucide-react';

import { CATEGORIES, LIBELLE_CATEGORIE, LIBELLE_RELANCE, RELANCES } from '@foyer/noyau';

import { Champ, Chargement, Erreur, Interrupteur, TRAIT } from '../composants/ui.js';
import { useAppli, useRessource } from '../lib/appli.js';
import {
  type EtatRappelsLocaux,
  essaiRappelLocal,
  estNatif,
  etatRappelsLocaux,
  reprogrammeRappels,
} from '../lib/rappels-locaux.js';
import { donnees } from '../lib/donnees.js';

const CANAUX = [
  { cle: 'email', libelle: 'Email', Icone: Mail },
  { cle: 'push', libelle: 'Notification téléphone', Icone: Smartphone },
  { cle: 'ntfy', libelle: 'ntfy (canal du foyer)', Icone: BellRing },
] as const;

const JOURS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

export function Notifications() {
  const { rafraichis, utilisateur, majUtilisateur } = useAppli();
  const reglages = useRessource(() => donnees.notifications());
  const clePush = useRessource(() => donnees.clePush());

  const [tests, setTests] = useState<Record<string, string>>({});
  const [occupe, setOccupe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ntfyUrl, setNtfyUrl] = useState('');
  const [ntfySujet, setNtfySujet] = useState('');
  const [ntfyJeton, setNtfyJeton] = useState('');

  if (reglages.chargement && !reglages.donnee) return <Chargement quoi="Chargement des réglages" />;
  if (reglages.erreur && !reglages.donnee) return <Erreur message={reglages.erreur} />;
  const etat = reglages.donnee!;

  async function change(corps: Record<string, unknown>) {
    setOccupe(true);
    try {
      const r = await donnees.majNotifications(corps);
      majUtilisateur(r.utilisateur);
      reglages.recharge();
      rafraichis();
    } finally {
      setOccupe(false);
    }
  }

  async function changePreference(corps: Record<string, unknown>) {
    setOccupe(true);
    try {
      await donnees.majPreference(corps);
      reglages.recharge();
    } finally {
      setOccupe(false);
    }
  }

  async function teste(canal: string) {
    setTests((t) => ({ ...t, [canal]: 'Envoi…' }));
    try {
      const r = await donnees.testNotification(canal);
      setTests((t) => ({ ...t, [canal]: r.message }));
    } catch (err) {
      setTests((t) => ({ ...t, [canal]: (err as Error).message }));
    }
  }

  /** Abonnement Web Push : c'est le navigateur qui décide, on lui demande. */
  async function abonne() {
    setMessage(null);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setMessage('Ce navigateur ne gère pas les notifications push.');
        return;
      }
      const cle = clePush.donnee?.clePublique;
      if (!cle) {
        setMessage('Le serveur n’a pas de clé VAPID : voir VAPID_PUBLIC dans la configuration.');
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setMessage('Notifications refusées par le navigateur. Les autres canaux continuent de fonctionner.');
        return;
      }
      const enregistrement = await navigator.serviceWorker.ready;
      const abonnement = await enregistrement.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: cle,
      });
      const brut = abonnement.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      await donnees.abonnePush({
        endpoint: brut.endpoint,
        keys: brut.keys,
        agent: navigator.userAgent.slice(0, 180),
      });
      reglages.recharge();
      setMessage('Cet appareil recevra les notifications.');
    } catch (err) {
      setMessage((err as Error).message);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>
          Notifications
        </h1>
      </div>
      <p className="chapeau">
        Trois canaux indépendants. Les rappels dus à la même heure arrivent regroupés en un seul
        message — quatre notifications d’affilée, personne ne le supporte. Ces réglages sont les
        vôtres : ils ne changent rien pour l’autre parent.
      </p>

      {message && <div className="note">{message}</div>}

      {/* ── Heures ────────────────────────────────────────────────────────── */}
      <div className="carte">
        <h3>Mes heures</h3>
        <div className="rangee">
          <Champ etiquette="Résumé quotidien" aide="Heure par défaut de tous les rappels.">
            <input
              type="time"
              value={etat.heureResume ?? '08:00'}
              onChange={(e) => void change({ heureResume: e.target.value })}
            />
          </Champ>
        </div>

        <div className="rangee">
          <Champ
            etiquette="Quand une tâche est en retard"
            aide="Le rappel de l’échéance part de toute façon ; ceci ne règle que l’insistance ensuite."
          >
            <select
              value={etat.relanceRetard}
              onChange={(e) => void change({ relanceRetard: e.target.value })}
            >
              {RELANCES.map((r) => (
                <option key={r} value={r}>
                  {LIBELLE_RELANCE[r]}
                </option>
              ))}
            </select>
          </Champ>
        </div>

        {etat.relanceRetard === 'jamais' && (
          <p className="note">
            Un retard important déclenche quand même l’alerte vers l’autre parent, si un seuil
            d’escalade est réglé sur la tâche. Prévenir n’est pas relancer.
          </p>
        )}

        <div className="ligne-reglage">
          <span className="texte">
            Ne voir que ce qui me concerne
            <div className="sourdine">
              Masque les tâches attribuées à quelqu’un d’autre, sur l’accueil comme dans les
              annonces. Une tâche dont le retard m’est remonté reste visible : c’est tout l’objet
              de l’escalade.
            </div>
          </span>
          <Interrupteur
            actif={etat.masqueAutres}
            etiquette="Ne voir que ce qui me concerne"
            onChange={(v) => void change({ masqueAutres: v })}
          />
        </div>

        <div className="ligne-reglage">
          <span className="texte">
            Quand l’autre ajoute une tâche
            <div className="sourdine">
              Un seul message, groupé, au plus toutes les cinq minutes. Pendant les heures de
              silence, l’annonce attend le matin plutôt que de faire vibrer le téléphone.
            </div>
          </span>
          <Interrupteur
            actif={etat.annoncesActives}
            etiquette="Annonces d’ajout de tâche"
            onChange={(v) => void change({ annoncesActives: v })}
          />
        </div>

        <div className="ligne-reglage">
          <span className="texte">
            Heures de silence
            <div className="sourdine">
              Un rappel qui tombe dans cette plage n’est pas perdu : il part à la fin du silence.
            </div>
          </span>
          <Interrupteur
            actif={etat.silence.actif}
            etiquette="Heures de silence"
            onChange={(v) => void change({ silenceActif: v })}
          />
        </div>

        {etat.silence.actif && (
          <div className="rangee">
            <Champ etiquette="De">
              <input type="time" value={etat.silence.de} onChange={(e) => void change({ silenceDe: e.target.value })} />
            </Champ>
            <Champ etiquette="À">
              <input type="time" value={etat.silence.a} onChange={(e) => void change({ silenceA: e.target.value })} />
            </Champ>
          </div>
        )}
      </div>

      {/* ── Canaux ────────────────────────────────────────────────────────── */}
      <div className="carte">
        <h3>Canaux</h3>
        {CANAUX.map(({ cle, libelle, Icone }) => {
          const disponible = etat.canauxDisponibles[cle];
          return (
            <div key={cle} className="ligne-reglage">
              <Icone strokeWidth={TRAIT} size={20} aria-hidden style={{ flex: '0 0 auto', color: 'var(--encre-2)' }} />
              <span className="texte">
                {libelle}
                <div className="sourdine">
                  {disponible
                    ? cle === 'push'
                      ? `${etat.appareils.n} appareil(s) abonné(s)`
                      : 'configuré'
                    : 'non configuré côté serveur'}
                </div>
                {tests[cle] && <div className="aide">{tests[cle]}</div>}
              </span>
              {cle === 'push' && (
                <button type="button" className="bouton secondaire" onClick={() => void abonne()}>
                  Autoriser
                </button>
              )}
              <button type="button" className="bouton icone" aria-label={`Tester ${libelle}`} onClick={() => void teste(cle)}>
                <Send strokeWidth={TRAIT} size={16} aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Rappels de l'application installée ────────────────────────────── */}
      <RappelsLocaux />

      {/* ── ntfy, canal partagé ───────────────────────────────────────────── */}
      {utilisateur?.role === 'parent' && (
        <div className="carte">
          <h3>ntfy — canal partagé du foyer</h3>
          <p className="sourdine">
            Un sujet commun que tout le monde voit. Se règle ici et non dans la configuration du
            serveur, parce qu’il appartient à la famille.
          </p>
          <div className="rangee">
            <Champ etiquette="URL du serveur">
              <input value={ntfyUrl} onChange={(e) => setNtfyUrl(e.target.value)} placeholder="https://ntfy.sh" />
            </Champ>
            <Champ etiquette="Sujet">
              <input value={ntfySujet} onChange={(e) => setNtfySujet(e.target.value)} placeholder="foyer-maine-du-four" />
            </Champ>
          </div>
          <Champ etiquette="Jeton" aide="Facultatif. Jamais réaffiché après enregistrement.">
            <input type="password" value={ntfyJeton} onChange={(e) => setNtfyJeton(e.target.value)} />
          </Champ>
          <button
            type="button"
            className="bouton secondaire"
            disabled={occupe}
            onClick={async () => {
              setOccupe(true);
              try {
                await donnees.majNtfy({ url: ntfyUrl || null, sujet: ntfySujet || null, jeton: ntfyJeton || null });
                setNtfyJeton('');
                reglages.recharge();
                setMessage('Canal ntfy enregistré.');
              } catch (err) {
                setMessage((err as Error).message);
              } finally {
                setOccupe(false);
              }
            }}
          >
            Enregistrer ntfy
          </button>
        </div>
      )}

      {/* ── Par catégorie ─────────────────────────────────────────────────── */}
      <div className="carte">
        <h3>Par catégorie</h3>
        <p className="sourdine">
          L’heure affichée est celle d’envoi réel. Un jour de la semaine regroupe les rappels de la
          catégorie — « les travaux le samedi matin ».
        </p>

        {CATEGORIES.filter((c) => c !== 'courses').map((categorie) => {
          const ligne = etat.reglages.find((r) => r.categorie === categorie);
          if (!ligne) return null;
          return (
            <details key={categorie} style={{ borderBottom: '1px solid var(--bordure)', padding: '8px 0' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                {LIBELLE_CATEGORIE[categorie]}
                <span className="sourdine" style={{ fontWeight: 400 }}>
                  {' — '}
                  {CANAUX.filter(({ cle }) => ligne.canaux[cle]?.actif)
                    .map(({ cle, libelle }) => {
                      const r = ligne.canaux[cle];
                      const heure = `${String(r.heure).padStart(2, '0')} h`;
                      const decale = r.heure !== r.heureVoulue;
                      return `${libelle.split(' ')[0].toLowerCase()} à ${heure}${decale ? ' (décalé)' : ''}${
                        r.jourSemaine ? ` le ${JOURS[r.jourSemaine]}` : ''
                      }`;
                    })
                    .join(', ') || 'aucun canal'}
                </span>
              </summary>

              {CANAUX.map(({ cle, libelle }) => {
                const r = ligne.canaux[cle];
                return (
                  <div key={cle} className="ligne-reglage" style={{ borderBottom: 0 }}>
                    <span className="texte">
                      {libelle}
                      {r.heure !== r.heureVoulue && (
                        <div className="sourdine">
                          Réglé à {r.heureVoulue} h, envoyé à {r.heure} h à cause des heures de silence.
                        </div>
                      )}
                    </span>
                    <input
                      type="time"
                      value={`${String(r.heureVoulue).padStart(2, '0')}:00`}
                      style={{ width: 110, flex: '0 0 auto' }}
                      aria-label={`Heure ${libelle} ${LIBELLE_CATEGORIE[categorie]}`}
                      onChange={(e) =>
                        void changePreference({
                          categorie,
                          canal: cle,
                          actif: r.actif,
                          heure: e.target.value,
                          jourSemaine: r.jourSemaine,
                        })
                      }
                    />
                    <select
                      value={r.jourSemaine ?? ''}
                      style={{ width: 120, flex: '0 0 auto' }}
                      aria-label={`Jour ${libelle} ${LIBELLE_CATEGORIE[categorie]}`}
                      onChange={(e) =>
                        void changePreference({
                          categorie,
                          canal: cle,
                          actif: r.actif,
                          heure: `${String(r.heureVoulue).padStart(2, '0')}:00`,
                          jourSemaine: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">chaque jour</option>
                      {JOURS.slice(1).map((j, i) => (
                        <option key={j} value={i + 1}>
                          {j}
                        </option>
                      ))}
                    </select>
                    <Interrupteur
                      actif={r.actif}
                      etiquette={`${libelle} pour ${LIBELLE_CATEGORIE[categorie]}`}
                      onChange={(v) =>
                        void changePreference({
                          categorie,
                          canal: cle,
                          actif: v,
                          heure: `${String(r.heureVoulue).padStart(2, '0')}:00`,
                          jourSemaine: r.jourSemaine,
                        })
                      }
                    />
                  </div>
                );
              })}
            </details>
          );
        })}
      </div>
    </>
  );
}

/**
 * Rappels programmés sur l'appareil.
 *
 * Cette section dit la vérité sur un mécanisme qu'on ne peut pas voir : combien
 * de rappels sont réellement en attente dans Android, quand tombe le prochain,
 * et ce qui a été écarté. Un rappel qui n'arrive pas sans qu'on sache pourquoi
 * est le pire des cas — d'où le bouton d'essai, qui tranche en dix secondes.
 *
 * Sur le web la section explique pourquoi elle ne s'applique pas, au lieu de
 * disparaître : son absence laisserait croire que le téléphone est couvert par
 * le Web Push, ce qui est justement faux dans l'application installée.
 */
function RappelsLocaux() {
  const { revision } = useAppli();
  const [etat, setEtat] = useState<EtatRappelsLocaux | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const taches = useRessource(() => donnees.taches({ statut: 'active' }));
  const vacances = useRessource(() => donnees.vacances());

  useEffect(() => {
    void etatRappelsLocaux().then(setEtat);
  }, [revision]);

  async function reprogramme() {
    setOccupe(true);
    setMessage(null);
    try {
      const r = await reprogrammeRappels(taches.donnee?.taches ?? [], vacances.donnee?.periodes ?? []);
      setEtat(r);
      setMessage(
        r.derniereErreur
          ? r.derniereErreur
          : `${r.programmees ?? 0} rappel(s) programmé(s)${r.ecartees > 0 ? `, ${r.ecartees} écarté(s) par le plafond` : ''}.`,
      );
    } finally {
      setOccupe(false);
    }
  }

  return (
    <div className="carte">
      <h3>Rappels de l’application</h3>

      {!estNatif() ? (
        <p className="sourdine">
          Ces rappels sont programmés par l’application Android sur l’appareil lui-même, sans passer par
          le serveur — la WebView d’Android ne sait pas recevoir de Web Push. Sur le web, c’est le Web
          Push ci-dessus qui s’applique.
        </p>
      ) : (
        <>
          <p className="sourdine">
            Programmés sur cet appareil, sans serveur : ils fonctionnent hors ligne. En revanche ils
            ignorent une modification faite ailleurs jusqu’à la prochaine reprogrammation.
          </p>

          <div className="ligne-reglage">
            <BellRing strokeWidth={TRAIT} size={20} aria-hidden style={{ flex: '0 0 auto', color: 'var(--encre-2)' }} />
            <span className="texte">
              Autorisation
              <div className="sourdine">{etat?.permission ?? '…'}</div>
            </span>
          </div>

          <div className="ligne-reglage">
            <span className="texte">
              En attente dans Android
              <div className="sourdine">
                {etat ? `${etat.enAttente} rappel(s)` : '…'}
                {etat?.ecartees ? ` · ${etat.ecartees} écarté(s) par le plafond` : ''}
              </div>
            </span>
          </div>

          {etat?.prochaine && (
            <div className="ligne-reglage">
              <span className="texte">
                Prochain rappel
                <div className="sourdine">
                  {etat.prochaine.date} — {etat.prochaine.titre}
                </div>
              </span>
            </div>
          )}

          {etat?.derniereSynchro && (
            <p className="aide">Dernière reprogrammation : {etat.derniereSynchro}</p>
          )}
          {etat?.derniereErreur && <p className="alerte">{etat.derniereErreur}</p>}
          {message && <p className="note">{message}</p>}

          <div className="actions">
            <button type="button" className="bouton" onClick={() => void reprogramme()} disabled={occupe}>
              Reprogrammer
            </button>
            <button
              type="button"
              className="bouton secondaire"
              disabled={occupe}
              onClick={async () => {
                const r = await essaiRappelLocal(10);
                setMessage(r === 'ok' ? 'Essai programmé dans 10 secondes.' : r);
              }}
            >
              <Send strokeWidth={TRAIT} size={16} aria-hidden />
              Essai dans 10 s
            </button>
          </div>
        </>
      )}
    </div>
  );
}
